import { useRef, useState } from "react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { PushToggle } from "@/components/PushToggle";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import {
  BookOpen, HandCoins, CreditCard, ArrowLeftRight, Eye, EyeOff, QrCode, Users, FileText, Settings,
  Wallet, Coins, Layers, Sprout, Megaphone, ChevronRight, Download, Loader2, User, IdCard, CalendarDays,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PhotoUploader } from "@/components/PhotoUploader";
import { InstallBanner } from "@/components/InstallApp";
import { AccountNo } from "@/components/AccountNo";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/lib/api";
import { fmtDate, rp, fmtAcc } from "@/lib/format";

const MENU = [
  { label: "Buku Tabungan", icon: BookOpen, to: "/app/tabungan", id: "savings", tone: "from-blue-500 to-blue-700 shadow-blue-500/40" },
  { label: "Pinjaman", icon: HandCoins, to: "/app/pinjaman", id: "loans", tone: "from-orange-400 to-orange-600 shadow-orange-500/40" },
  { label: "Pembayaran", icon: CreditCard, to: "/app/transaksi/bayar", id: "payments", tone: "from-red-500 to-red-700 shadow-red-500/40" },
  { label: "Transfer Online", icon: ArrowLeftRight, to: "/app/transaksi/transfer?ke=anggota", id: "transfer", tone: "from-violet-500 to-violet-700 shadow-violet-500/40" },
  { label: "QR Saya", sub: "(Terima Transfer)", icon: QrCode, action: "qr", id: "my-qr", tone: "from-green-500 to-green-700 shadow-green-500/40" },
  { label: "Profil Anggota", icon: Users, to: "/app/profil", id: "profile", tone: "from-indigo-500 to-violet-700 shadow-indigo-500/40" },
  { label: "Riwayat Transaksi", icon: FileText, to: "/app/transaksi", id: "history", tone: "from-sky-400 to-blue-600 shadow-sky-500/40" },
  { label: "Pengaturan", icon: Settings, to: "/app/profil", id: "settings", tone: "from-pink-500 to-rose-600 shadow-pink-500/40" },
];

const Swoosh = () => (
  <svg className="absolute inset-0 h-full w-full pointer-events-none" viewBox="0 0 400 200" preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 0 H150 C90 10 40 30 0 60 Z" fill="#dc2626" />
    <path d="M0 0 H175 C110 15 50 45 0 80 V60 C40 30 90 10 150 0 Z" fill="#1d4ed8" opacity="0.9" />
    <path d="M0 200 V185 C120 165 260 190 400 150 V200 Z" fill="#1d4ed8" />
    <path d="M60 200 C160 180 280 195 400 165 V172 C290 200 170 192 90 200 Z" fill="#dc2626" />
  </svg>
);

const CardRow = ({ icon: Icon, label, children, testid }) => (
  <div className="flex items-center gap-1.5 text-[11px] text-slate-700 whitespace-nowrap">
    <Icon className="h-3.5 w-3.5 text-slate-600 shrink-0" strokeWidth={1.8} />
    <span className="w-[68px] shrink-0">{label}</span>
    <span className="font-bold text-slate-900 min-w-0 flex items-center" data-testid={testid}>: <span className="ml-1">{children}</span></span>
  </div>
);

export const MemberCard = ({ user, cardRef }) => (
  <div ref={cardRef} className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white via-slate-50 to-blue-50 dark:from-[#0b1326] dark:via-[#111a2e] dark:to-[#0f1e3d] border border-white dark:border-blue-900/60 shadow-xl shadow-blue-900/20 fade-up" data-testid="member-card">
    <Swoosh />
    <div className="relative flex items-center justify-between gap-2 px-4 pt-6">
      <div className="font-display font-extrabold text-blue-900 dark:text-blue-200 text-[13px] tracking-wide pl-2 whitespace-nowrap">KARTU ANGGOTA DIGITAL</div>
      <div className="bg-white keep-white rounded-lg px-1 py-0.5 shrink-0 shadow-sm">
        <img src="/brand-banner.webp" alt="Credit Union Tuah Talino" className="h-9 w-auto object-contain" />
      </div>
    </div>
    <div className="relative flex items-start gap-3 px-4 pt-4 pb-7">
      <PhotoUploader className="h-[80px] w-[62px] rounded-xl border-2 border-white shadow text-2xl shrink-0" testid="member-card-photo" />
      <div className="w-px self-stretch bg-slate-300 dark:bg-slate-600" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="font-display font-extrabold text-slate-900 text-lg leading-tight break-words" data-testid="member-card-name">{user.name}</div>
        <CardRow icon={User} label="No. Anggota" testid="member-card-number">{user.member_no}</CardRow>
        {user.account_no && (
          <CardRow icon={IdCard} label="No. Rekening">
            <AccountNo value={user.account_no} className="text-slate-900 text-[11px] tracking-normal font-sans" testid="member-card-account" />
          </CardRow>
        )}
        <CardRow icon={CalendarDays} label="Bergabung">{fmtDate(user.joined_at)}</CardRow>
        <span data-testid="member-card-status" className={`inline-block text-[11px] font-bold px-4 py-0.5 rounded-full text-white ${user.status === "aktif" ? "bg-green-600" : "bg-slate-500"}`}>
          {user.status === "aktif" ? "Aktif" : "Nonaktif"}
        </span>
      </div>
      <div className="absolute right-3 bottom-3 bg-white keep-white p-1.5 rounded-lg shadow-md" data-testid="member-card-qr">
        <QRCodeSVG value={`CUTUAHTALINO:${user.member_no}`} size={50} />
      </div>
    </div>
  </div>
);

const BalanceCard = ({ b }) => {
  const [show, setShow] = useState(false);
  const items = [["pokok", "Pokok", Coins], ["wajib", "Wajib", Layers], ["sukarela", "Sukarela", Sprout]];
  return (
    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 text-white p-4 shadow-xl shadow-blue-900/30 fade-up" style={{ animationDelay: "80ms" }}>
      <svg className="absolute right-4 top-3 h-20 w-32 opacity-40" viewBox="0 0 120 80" aria-hidden="true">
        {[0, 1, 2, 3].map((i) => <rect key={i} x={10 + i * 26} y={60 - i * 14} width="16" height={20 + i * 14} rx="3" fill="#93c5fd" />)}
        <path d="M8 50 L40 36 L66 40 L104 10" stroke="#e0f2fe" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M96 8 L108 8 L106 20" stroke="#e0f2fe" strokeWidth="4" fill="none" strokeLinecap="round" />
      </svg>
      <div className="relative flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center shadow-lg shrink-0"><Wallet className="h-7 w-7" /></div>
        <div className="border-l border-white/40 pl-4">
          <div className="flex items-center gap-2 text-sm text-blue-50">
            Total Saldo Tabungan
            <button onClick={() => setShow(!show)} data-testid="home-toggle-balance" className="p-1 rounded-full hover:bg-white/15" aria-label="Tampilkan saldo">
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="text-[26px] font-extrabold font-display leading-tight" data-testid="home-total-balance">{show ? rp(b?.total) : "Rp •••••••"}</div>
        </div>
      </div>
      <div className="relative grid grid-cols-3 mt-4 rounded-2xl bg-white/10 border border-white/15 backdrop-blur py-2.5">
        {items.map(([k, l, Icon], i) => (
          <div key={k} className={`flex items-center gap-1.5 px-2 ${i ? "border-l border-white/25" : ""}`}>
            <Icon className="h-4 w-4 shrink-0 text-blue-100" strokeWidth={1.8} />
            <div className="min-w-0">
              <div className="text-[10px] text-blue-100">{l}</div>
              <div className="text-[11px] font-bold whitespace-nowrap tracking-tight" data-testid={`home-balance-${k}`}>{show ? rp(b?.[k]) : "•••••"}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MyQrDialog = ({ user, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-xs" data-testid="my-qr-dialog">
      <DialogHeader>
        <DialogTitle className="text-center">QR Saya</DialogTitle>
        <DialogDescription className="text-center">Tunjukkan QR atau bagikan nomor rekening ini untuk menerima transfer antar anggota.</DialogDescription>
      </DialogHeader>
      <div className="mx-auto bg-white keep-white p-3 rounded-2xl shadow-md">
        <QRCodeSVG value={`CUTUAHTALINO:REK:${user.account_no || ""}`} size={190} />
      </div>
      <div className="text-center">
        <div className="font-bold text-slate-900">{user.name}</div>
        <AccountNo value={user.account_no} className="text-blue-800 text-lg justify-center" testid="my-qr-account" />
        <div className="text-[11px] text-slate-500">Simpanan Sukarela • {fmtAcc(user.account_no)}</div>
      </div>
    </DialogContent>
  </Dialog>
);

const CardDownload = ({ cardRef, user }) => {
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const url = await toPng(cardRef.current, { pixelRatio: 4, cacheBust: true, filter: (n) => !n.dataset?.exportSkip });
      const a = document.createElement("a");
      a.href = url;
      a.download = `kartu-anggota-${user.member_no}.png`;
      a.click();
      toast.success("Kartu anggota tersimpan sebagai gambar");
    } catch {
      toast.error("Gagal menyimpan kartu, coba lagi");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={save} disabled={busy} data-testid="member-card-download"
      className="-mt-1 w-full flex items-center justify-center gap-2 text-sm font-bold text-blue-800 dark:text-blue-200 bg-white border border-blue-100 dark:border-blue-900/60 rounded-2xl py-2.5 shadow-sm hover:bg-blue-50 active:scale-[0.98] transition-transform disabled:opacity-60">
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Simpan Kartu (Gambar)
    </button>
  );
};

export default function Beranda() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data } = useApi("/me/summary");
  const [qrOpen, setQrOpen] = useState(false);
  const cardRef = useRef(null);

  return (
    <div>
      <div className="relative bg-gradient-to-b from-blue-800 via-blue-700 to-blue-600 px-4 pb-16 -mt-px overflow-hidden" data-testid="home-hero">
        <svg className="absolute right-0 top-0 h-28 w-48 opacity-90" viewBox="0 0 200 120" aria-hidden="true">
          <path d="M200 10 C150 40 110 60 40 70 C110 70 160 55 200 35 Z" fill="#dc2626" />
          <path d="M200 0 C160 25 120 40 70 50 C130 48 170 35 200 18 Z" fill="#ffffff" opacity="0.85" />
          <path d="M90 120 L130 82 L145 95 L165 70 L200 105 V120 Z" fill="#ffffff" opacity="0.9" />
        </svg>
      </div>

      <div className="px-4 -mt-16 space-y-4 relative z-10 pt-2">
        <MemberCard user={user} cardRef={cardRef} />
        <CardDownload cardRef={cardRef} user={user} />
        <PushToggle compact />
        <BalanceCard b={data?.balances} />
        <InstallBanner />

        <div className="grid grid-cols-4 gap-2.5">
          {MENU.map(({ label, sub, icon: Icon, to, action, id, tone }, i) => (
            <button key={id} onClick={() => (action === "qr" ? setQrOpen(true) : navigate(to))} data-testid={`home-menu-${id}`}
              className="bg-white border border-slate-100 rounded-2xl py-3 px-1 min-h-[104px] flex flex-col items-center justify-center gap-2 shadow-md shadow-slate-200/70 hover:-translate-y-0.5 active:scale-[0.97] transition-transform fade-up"
              style={{ animationDelay: `${120 + i * 40}ms` }}>
              <span className={`h-11 w-11 rounded-2xl bg-gradient-to-b ${tone} text-white flex items-center justify-center shadow-lg`}><Icon className="h-6 w-6" strokeWidth={1.9} /></span>
              <span className="text-[11px] font-bold text-slate-900 text-center leading-tight">{label}{sub && <span className="block font-medium text-slate-600 text-[10px]">{sub}</span>}</span>
            </button>
          ))}
        </div>

        {data?.active_loan && (
          <button onClick={() => navigate("/app/pinjaman")} data-testid="home-active-loan" className="w-full text-left bg-gradient-to-r from-orange-500 to-amber-400 text-white rounded-2xl p-4 shadow-lg shadow-orange-500/30">
            <div className="text-xs text-white/90 font-semibold">Angsuran berikutnya</div>
            <div className="flex justify-between items-end mt-1">
              <div className="font-bold text-lg">{rp(data.active_loan.monthly_installment)}</div>
              <div className="text-xs text-white/90">Jatuh tempo {fmtDate(data.active_loan.next_due)}</div>
            </div>
          </button>
        )}

        <button onClick={() => navigate("/app/notifikasi")} data-testid="home-info-banner"
          className="w-full relative overflow-hidden flex items-center gap-3 text-left bg-gradient-to-r from-blue-50 to-white border border-blue-100 rounded-2xl p-4 shadow-sm">
          <Megaphone className="h-10 w-10 text-blue-700 shrink-0 -rotate-12" strokeWidth={1.7} />
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-blue-900 text-[15px]">Informasi CU TUAH TALINO</div>
            <div className="text-xs text-slate-600">Pembayaran angsuran kini bisa melalui aplikasi.</div>
          </div>
          <ChevronRight className="h-5 w-5 text-blue-900" />
        </button>
      </div>
      <MyQrDialog user={user} open={qrOpen} onOpenChange={setQrOpen} />
    </div>
  );
}
